import { IBankAccount } from '@commenda/api';
import { Badge, Icons } from '@commenda/kakashi';
import { cn } from '@commenda/utils';
import { ReactNode } from 'react';

import { formatAccountType } from '../../bankAccount/utils';

function formatSubType(subType: string): string {
  return formatAccountType(subType) ?? subType;
}

export function formatAccountDescription(account: IBankAccount): string {
  const parts: string[] = [];

  if (account.accountNumberMask) {
    parts.push(`•••• ${account.accountNumberMask}`);
  }

  if (account.subType) {
    parts.push(formatSubType(account.subType));
  }

  return parts.join(' · ');
}

interface BankAccountCardProps {
  account: IBankAccount;
  onClick?: (account: IBankAccount) => void;
  showBadges?: boolean;
  trailing?: ReactNode;
  className?: string;
}

export const BankAccountCard = ({
  account,
  onClick,
  showBadges = false,
  trailing,
  className,
}: BankAccountCardProps) => (
  <div
    className={cn(
      'flex items-center justify-between px-4 py-3 border-b transition-colors',
      onClick &&
        'cursor-pointer hover:bg-bg-secondary hover:rounded-md hover:border-b-transparent',
      className
    )}
    onClick={onClick ? () => onClick(account) : undefined}
  >
    <div className="flex items-center gap-3">
      <div className="size-9 rounded-full border border-border-primary flex items-center justify-center shrink-0">
        <Icons.Bank className="size-4 text-fg-secondary" />
      </div>
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <span className="txt-small-bold text-text-primary">
            {account.bankName}
          </span>
          {showBadges && account.isRemittanceAccount && (
            <Badge variant="blue" size="sm">
              Default
            </Badge>
          )}
          {showBadges && account.plaidId && (
            <Badge variant="blue" size="sm">
              Connected via Plaid
            </Badge>
          )}
        </div>
        <span className="txt-extra-small-regular text-text-tertiary">
          {formatAccountDescription(account)}
        </span>
      </div>
    </div>

    {trailing && <div onClick={(e) => e.stopPropagation()}>{trailing}</div>}
  </div>
);
